// CareLogix™ ORACLE — FundingOpportunityGraph (Phase 33 §4)
// Animated graph: priority vs probability scatter + deadline pressure halo.
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import { fundingApi } from "@/api/phase33Client";

export default function FundingOpportunityGraph({ bucket = "all", limit = 18, testid = "funding-opportunity-graph" }) {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fundingApi.opportunities({ bucket, limit })
      .then((r) => { if (!cancelled) setOpps(r.data?.opportunities || []); })
      .catch(() => { if (!cancelled) setErr("Could not load funding opportunities."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bucket, limit]);

  const W = 720, H = 360;
  const padX = 36, padY = 28;
  const nodes = useMemo(() => opps.map((o) => {
    const x = padX + (o.scores.probability_score / 100) * (W - padX * 2);
    const y = H - padY - (o.scores.priority_score / 100) * (H - padY * 2);
    const size = 6 + (o.scores.match_score || 50) / 8;
    const urgent = o.days_to_deadline <= 30;
    return { ...o, x, y, size, urgent };
  }), [opps]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>FUNDING OPPORTUNITY GRAPH™</div>
          <h3 style={S.title}>Priority × Probability · {opps.length} opportunities</h3>
        </div>
        <div style={S.legend}>
          <span style={S.legendChip}><span style={{ ...S.legendDot, background: PALETTE.greenAccent }} />Priority ↑</span>
          <span style={S.legendChip}><span style={{ ...S.legendDot, background: PALETTE.gold }} />Match score size</span>
          <span style={S.legendChip}><span style={{ ...S.legendDot, background: PALETTE.red }} />≤0 ≤3 0d deadline</span>
        </div>
      </header>

      {loading && <div style={S.loading}>Loading funding graph…</div>}
      {err && <div style={S.err} data-testid={`${testid}-error`}>{err}</div>}

      {!loading && !err && (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" data-testid={`${testid}-svg`}>
          {/* axes */}
          <line x1={padX} y1={padY} x2={padX} y2={H - padY} stroke="rgba(197,160,89,0.20)" />
          <line x1={padX} y1={H - padY} x2={W - padX} y2={H - padY} stroke="rgba(197,160,89,0.20)" />
          <text x={padX} y={padY - 8} fontSize="10" fill={PALETTE.gold} fontFamily="'Space Mono', monospace">Priority ↑</text>
          <text x={W - padX} y={H - 6} textAnchor="end" fontSize="10" fill={PALETTE.gold} fontFamily="'Space Mono', monospace">Probability →</text>
          {nodes.map((n, i) => (
            <g key={n.opportunity_id}>
              {n.urgent && (
                <motion.circle cx={n.x} cy={n.y} r={n.size + 8} fill="none" stroke={PALETTE.red} strokeWidth="1" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.55, 0] }} transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.07 }} />
              )}
              <motion.circle
                cx={n.x} cy={n.y} r={n.size}
                fill={scoreColor(n.scores.priority_score)}
                fillOpacity="0.75"
                stroke={PALETTE.goldBright} strokeWidth="1"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                data-testid={`${testid}-node-${n.opportunity_id}`}
              >
                <title>{n.title} · priority {n.scores.priority_score} · prob {n.scores.probability_score}</title>
              </motion.circle>
            </g>
          ))}
        </svg>
      )}
      <footer style={S.foot}>Match/probability/priority are decision-support · Not award guarantees.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 12 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 15 },
  legend:   { display: "flex", gap: 6, flexWrap: "wrap" },
  legendChip:{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 10, color: PALETTE.slate3, padding: "3px 8px", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 999 },
  legendDot:{ width: 8, height: 8, borderRadius: "50%" },
  loading:  { color: PALETTE.slate4, fontSize: 12.5 },
  err:      { color: PALETTE.red, fontSize: 12.5, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8 },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
